import React, { useState, useEffect } from 'react';
import { Box, Tabs, TabList, Tab, TabPanels, TabPanel, Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Input, FormControl, FormLabel } from '@chakra-ui/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import InvoiceCard from './InvoiceCard';

const PaymentPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All Invoices');
  const [submittedInvoices, setSubmittedInvoices] = useState([]);
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [rejectedInvoices, setRejectedInvoices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accountDetails, setAccountDetails] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    sortCode: ''
  });
  const [hasAccountDetails, setHasAccountDetails] = useState(false);

  useEffect(() => {
    const fetchSubmittedInvoices = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-submitted-invoices`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Submitted invoices:', response.data);
        setSubmittedInvoices(response.data.data);
      } catch (error) {
        console.error('Error fetching submitted invoices:', error);
      }
    };


    const fetchApprovedInvoices = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-approved-invoices`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Approved invoices:', response.data);
        setApprovedInvoices(response.data.data);
      } catch (error) {
        console.error('Error fetching approved invoices:', error);
      }
    };

    const fetchRejectedInvoices = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-rejected-invoices`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Rejected invoices:', response.data);
        setRejectedInvoices(response.data.data);
      } catch (error) {
        console.error('Error fetching rejected invoices:', error);
      }
    };

    const fetchApplicantDetails = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-applicant-details`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );

        const data = response.data.data;
        if (data.accountName && data.accountNumber && data.bankName && data.sortCode) {
          setAccountDetails({
            accountName: data.accountName,
            accountNumber: data.accountNumber,
            bankName: data.bankName,
            sortCode: data.sortCode
          });
          setHasAccountDetails(true);
        }
      } catch (error) {
        console.error('Error fetching applicant details:', error);
      }
    };

    if (selectedCategory === 'All Invoices') {
      fetchSubmittedInvoices();
    } else if (selectedCategory === 'Approved Invoice') {
      fetchApprovedInvoices();
    } else if (selectedCategory === 'Rejected Invoice') {
      fetchRejectedInvoices();
    }

    fetchApplicantDetails();
  }, [selectedCategory]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountDetails({ ...accountDetails, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/applicant/update-bank-details`,
        accountDetails,
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );

      if (response.data.status === "ok") {
        console.log('Bank details saved successfully');
        toast.success("Bank details saved successfully");
        setHasAccountDetails(true);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving bank details:', error);
    }
  };

  const categories = ['All Invoices', 'Approved Invoice', 'Rejected Invoice'];

  const sortInvoicesByNumber = (invoices) => {
    return [...invoices].sort((a, b) => {
      const numA = parseInt(a.invoiceNo.slice(6)); // Get the last 3 digits
      const numB = parseInt(b.invoiceNo.slice(6));
      return numB - numA; // Sort in descending order
    });
  };

  return (
    <Box p={4}>
      <Tabs variant="unstyled" onChange={(index) => setSelectedCategory(categories[index])}>
        <TabList mr={4} ml={4} borderBottom="1px solid" borderColor="gray.200" mb={4}>
          {categories.map((category, index) => (
            <Tab
              key={index}
              color="black"
              _selected={{ fontWeight: "bold", color: 'black', borderBottom: '3px solid', borderColor: 'blue.500', borderRadius: 'none' }}
              _focus={{ boxShadow: 'none' }}
              _hover={{ background: 'none' }}
              py={2}
              px={3}
              mr={4}
              mb={0.5}
            >
              {category}
            </Tab>
          ))}
        </TabList>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px', marginRight: '20px' }}>
          <Button colorScheme="blue" mt={4} onClick={handleOpenModal}>
            {hasAccountDetails ? 'Edit Bank Details' : 'Add Bank Details'}
          </Button>
        </div>

        <TabPanels>
          {categories.map((category, index) => (
            <TabPanel key={index}>
              {category === 'All Invoices' && submittedInvoices.length === 0 && (
                <Text textAlign="center" mt={4} color="gray.500">
                  No submitted invoices found
                </Text>
              )}
              {category === 'All Invoices' && sortInvoicesByNumber(submittedInvoices).map((invoice) => (
                <InvoiceCard key={invoice._id} invoice={invoice} onClose={handleCloseModal} isApplicant={true} />
              ))}
              {category === 'Approved Invoice' && sortInvoicesByNumber(approvedInvoices).map((invoice) => (
                <InvoiceCard key={invoice._id} invoice={invoice} onClose={handleCloseModal} isApplicant={true} />
              ))}
              {category === 'Rejected Invoice' && sortInvoicesByNumber(rejectedInvoices).map((invoice) => (
                <InvoiceCard key={invoice._id} invoice={invoice} onClose={handleCloseModal} isApplicant={true}/>
              ))}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{hasAccountDetails ? 'Edit Account Details' : 'Add Account Details'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="accountName" mb={4}>
              <FormLabel>Account Name</FormLabel>
              <Input name="accountName" value={accountDetails.accountName} onChange={handleInputChange} />
            </FormControl>
            <FormControl id="accountNumber" mb={4}>
              <FormLabel>Account Number</FormLabel>
              <Input name="accountNumber" value={accountDetails.accountNumber} onChange={handleInputChange} />
            </FormControl>
            <FormControl id="bankName" mb={4}>
              <FormLabel>Bank Name</FormLabel>
              <Input name="bankName" value={accountDetails.bankName} onChange={handleInputChange} />
            </FormControl>
            <FormControl id="sortCode" mb={4}>
              <FormLabel>Sort Code</FormLabel>
              <Input name="sortCode" value={accountDetails.sortCode} onChange={handleInputChange} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
              Save
            </Button>
            <Button variant="ghost" onClick={handleCloseModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PaymentPage;
